import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { X, Save, AlertTriangle, Sparkles, Music2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const BLOCKED_PHRASE = "concrete jungle";

// Simple multi-syllable rhyme detection
const findRhymeSchemes = (text) => {
  if (!text) return [];
  const lines = text.split("\n").filter((l) => l.trim());
  const rhymes = [];

  // Get last word of each line
  const endWords = lines.map((line) => {
    const words = line.trim().split(/\s+/);
    return words[words.length - 1]?.toLowerCase().replace(/[^a-z]/g, "") || "";
  });

  // Find rhyming pairs (simple vowel pattern matching)
  const getVowelPattern = (word) => {
    return word.replace(/[^aeiou]/g, "");
  };

  for (let i = 0; i < endWords.length; i++) {
    for (let j = i + 1; j < endWords.length; j++) {
      const pattern1 = getVowelPattern(endWords[i]);
      const pattern2 = getVowelPattern(endWords[j]);
      if (pattern1.length >= 2 && pattern1 === pattern2) {
        rhymes.push({ word1: endWords[i], word2: endWords[j], lines: [i, j] });
      }
    }
  }

  return rhymes;
};

export const TrackInspector = ({ track, onUpdate, onClose }) => {
  const [formData, setFormData] = useState({
    title: "",
    bpm: "",
    key: "",
    genre: "",
    isrc: "",
    tags: [],
    lyrics: "",
  });
  const [newTag, setNewTag] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [blockedPhraseDetected, setBlockedPhraseDetected] = useState(false);
  const [phoneticFogEnabled, setPhoneticFogEnabled] = useState(false);
  const [rhymeSchemes, setRhymeSchemes] = useState([]);

  useEffect(() => {
    if (track) {
      setFormData({
        title: track.title || "",
        bpm: track.bpm || "",
        key: track.key || "",
        genre: track.genre || "",
        isrc: track.isrc || "",
        tags: track.tags || [],
        lyrics: track.lyrics || "",
      });
    }
  }, [track]);

  // Check for blocked phrase
  useEffect(() => {
    const hasBlocked = formData.lyrics.toLowerCase().includes(BLOCKED_PHRASE);
    setBlockedPhraseDetected(hasBlocked);
  }, [formData.lyrics]);

  // Find rhyme schemes when enabled
  useEffect(() => {
    if (phoneticFogEnabled) {
      const schemes = findRhymeSchemes(formData.lyrics);
      setRhymeSchemes(schemes);
    } else {
      setRhymeSchemes([]);
    }
  }, [formData.lyrics, phoneticFogEnabled]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tagToRemove),
    }));
  };

  const handleSave = async () => {
    if (blockedPhraseDetected) {
      toast.error("Cannot save: Lyrics contain blocked phrase 'concrete jungle'");
      return;
    }

    setIsSaving(true);
    const result = await onUpdate(track.id, {
      ...formData,
      bpm: formData.bpm ? parseInt(formData.bpm) : null,
    });

    if (result.success) {
      toast.success("Track metadata saved successfully");
    } else {
      toast.error(result.error || "Failed to save track");
    }
    setIsSaving(false);
  };

  // Highlight blocked phrase in lyrics
  const renderLyricsWithHighlight = () => {
    if (!blockedPhraseDetected) return formData.lyrics;

    const regex = new RegExp(`(${BLOCKED_PHRASE})`, "gi");
    const parts = formData.lyrics.split(regex);

    return parts.map((part, i) =>
      part.toLowerCase() === BLOCKED_PHRASE ? (
        <span key={i} className="bg-red-500/30 text-meta-danger line-through">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="bg-meta-surface border border-meta-border h-full"
      data-testid="track-inspector"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-meta-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Music2 className="text-meta-neon" size={20} />
          <div>
            <h2 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted">
              Track Inspector
            </h2>
            <p className="font-sans font-semibold text-meta-text">{track.title}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-meta-elevated transition-colors"
          data-testid="close-inspector"
        >
          <X size={18} className="text-meta-text-secondary" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6 overflow-auto max-h-[calc(100vh-200px)]">
        {/* Metadata Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-meta-text-muted font-mono text-xs uppercase">Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className="bg-meta-base border-meta-border text-meta-text font-mono focus:ring-meta-neon focus:border-meta-neon"
              data-testid="input-title"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-meta-text-muted font-mono text-xs uppercase">BPM</Label>
            <Input
              type="number"
              value={formData.bpm}
              onChange={(e) => handleChange("bpm", e.target.value)}
              className="bg-meta-base border-meta-border text-meta-text font-mono focus:ring-meta-neon focus:border-meta-neon"
              data-testid="input-bpm"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-meta-text-muted font-mono text-xs uppercase">Key</Label>
            <Input
              value={formData.key}
              onChange={(e) => handleChange("key", e.target.value)}
              className="bg-meta-base border-meta-border text-meta-text font-mono focus:ring-meta-neon focus:border-meta-neon"
              data-testid="input-key"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-meta-text-muted font-mono text-xs uppercase">Genre</Label>
            <Input
              value={formData.genre}
              onChange={(e) => handleChange("genre", e.target.value)}
              className="bg-meta-base border-meta-border text-meta-text font-mono focus:ring-meta-neon focus:border-meta-neon"
              data-testid="input-genre"
            />
          </div>
          <div className="col-span-2 space-y-2">
            <Label className="text-meta-text-muted font-mono text-xs uppercase">ISRC</Label>
            <Input
              value={formData.isrc}
              onChange={(e) => handleChange("isrc", e.target.value)}
              placeholder="USRC12345678"
              className="bg-meta-base border-meta-border text-meta-text font-mono focus:ring-meta-neon focus:border-meta-neon"
              data-testid="input-isrc"
            />
          </div>
        </div>

        {/* Tags */}
        <div className="space-y-2">
          <Label className="text-meta-text-muted font-mono text-xs uppercase">Tags</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.tags.map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="bg-meta-neon-dim text-meta-neon border-none font-mono cursor-pointer hover:bg-meta-neon hover:text-meta-base transition-colors"
                onClick={() => handleRemoveTag(tag)}
              >
                {tag} ×
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
              placeholder="Add tag..."
              className="bg-meta-base border-meta-border text-meta-text font-mono focus:ring-meta-neon focus:border-meta-neon"
              data-testid="input-new-tag"
            />
            <Button
              onClick={handleAddTag}
              variant="outline"
              className="border-meta-border text-meta-text hover:border-meta-neon hover:text-meta-neon"
              data-testid="add-tag-btn"
            >
              Add
            </Button>
          </div>
        </div>

        {/* Lyric Matrix */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-meta-text-muted font-mono text-xs uppercase">
              Lyric Matrix
            </Label>
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-meta-neon" />
              <span className="text-xs text-meta-text-muted">Phonetic Fog</span>
              <Switch
                checked={phoneticFogEnabled}
                onCheckedChange={setPhoneticFogEnabled}
                className="data-[state=checked]:bg-meta-neon"
                data-testid="phonetic-fog-toggle"
              />
            </div>
          </div>

          {/* Blocked phrase warning */}
          {blockedPhraseDetected && (
            <div className="danger-banner" data-testid="blocked-phrase-warning">
              <AlertTriangle size={18} />
              <span className="font-mono text-sm">
                Blocked phrase detected: "{BLOCKED_PHRASE}" - Cannot save until removed
              </span>
            </div>
          )}

          {/* Rhyme schemes display */}
          {phoneticFogEnabled && rhymeSchemes.length > 0 && (
            <div className="bg-meta-neon-dim border border-meta-neon/30 p-3">
              <p className="text-xs font-mono text-meta-neon mb-2">
                Rhyme Schemes Detected:
              </p>
              <div className="flex flex-wrap gap-2">
                {rhymeSchemes.map((scheme, idx) => (
                  <span
                    key={idx}
                    className="text-xs font-mono bg-meta-base px-2 py-1 text-meta-text"
                  >
                    {scheme.word1} ↔ {scheme.word2}
                  </span>
                ))}
              </div>
            </div>
          )}

          <Textarea
            value={formData.lyrics}
            onChange={(e) => handleChange("lyrics", e.target.value)}
            className="lyric-matrix min-h-[200px] text-meta-text"
            placeholder="Write your lyrics here..."
            data-testid="lyrics-textarea"
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3 pt-4 border-t border-meta-border">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-meta-border text-meta-text hover:border-meta-neon hover:text-meta-neon"
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || blockedPhraseDetected}
            className="bg-meta-neon text-meta-base hover:shadow-neon disabled:opacity-50"
            data-testid="save-track-btn"
          >
            <Save size={16} className="mr-2" />
            {isSaving ? "Saving..." : "Save Metadata"}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
